import React, { useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import 'react-calendar/dist/Calendar.css';
import '@/assets/styles/calendar-schedule.scss';

import { storeConsultation } from '@/store/consultationDuck';
import { useSnackBar } from '@/store/snackbar/snackbarContext';
import avatarDoctorImage from '@/assets/images/avatar_doctor.jpg';
import locationImage from '@/assets/images/icon_location.svg';
import timeImage from '@/assets/images/icon_time_fill.svg';
import arrowLeft from '@/assets/images/arrow_left.png';
import { Button } from '@/components/uikit';
import { useQuery } from '@/utils/hooks';
import { isPhoneNumberValid, currencyHelper } from '@/utils/helper';
import doctorApi from '@/api/Doctor';
import { useTranslate } from '@/locale';

import { RootState } from '@/store/rootReducer';
import { Doctor, Schedule } from '@/types';
import { MainLayout } from '@/components/layout';
import DateSection from './components/DateSection';
import TimeSection from './components/TimeSection';

const useStyles = makeStyles({
  title: { fontSize: 18, margin: 0 },
  name: { margin: 0, fontWeight: 700 },
  specialist: { margin: 0, color: '#585E5F', fontSize: 14 },
  city: { margin: 0, color: '#F58F63', fontSize: 14, fontWeight: 600 },
});

const Divider = () => <Box bgcolor="#FAFAFA" height="8px" width="100%" />;

const HEADER_BOXSHADOW_VALUE = '0 6px 6px -8px rgba(0, 0, 0, 0.4)';
const FOOTER_BOXSHADOW_VALUE = '0 -6px 6px -8px rgba(0, 0, 0, 0.4)';

export default function ConsultationSchedule() {
  const {
    currentLanguage,
    translate,
    checkStorageWhenLngQueryIsNotExist,
  } = useTranslate();
  const history = useHistory();
  const dispatch = useDispatch();
  const consultationData = useSelector(
    (state: RootState) => state.consultation
  );
  const { doctorID } = useParams<{ doctorID: string }>();
  const { showSnackBar } = useSnackBar();
  const query = useQuery();
  const patientId = query.get('patient_id');
  const patientName = query.get('patient_name');
  const patientPhoneNumber = query.get('patient_phone_number');
  const style = useStyles();
  const [selectedDate, setSelectedDate] = useState<Partial<Schedule>>({
    date: consultationData?.consultation_date || '',
    is_with_appointment: consultationData?.is_with_appointment,
  });
  const [selectedTimes, setSelectedTimes] = useState<string[]>(
    consultationData.consultation_time || []
  );
  const [doctorData, setDoctorData] = useState<Doctor | undefined>(
    consultationData.doctor
  );

  const handleFetchDoctor = async (id: string) => {
    try {
      const response = await doctorApi.getDoctorDetail(id);
      setDoctorData(response.data);
    } catch (error) {
      if (error.status === 400) {
        showSnackBar('Dokter tidak ditemukan', 'error');
      } else {
        showSnackBar('Terjadi Kesalahan', 'error');
      }
    }
  };

  useEffect(() => {
    // FIXME: prevent double fetching
    if (!doctorData && doctorID) {
      handleFetchDoctor(doctorID);
    }
  }, []);

  useEffect(() => {
    // ANCHOR: initiate language
    moment.locale(currentLanguage);
    checkStorageWhenLngQueryIsNotExist();
  }, [currentLanguage]);

  useEffect(() => {
    // ANCHOR: set patient data from params
    if (patientId && patientName && patientPhoneNumber) {
      dispatch(
        storeConsultation({
          patient_id: Number(patientId),
          patient_name: patientName,
          // NOTE: make sure if phone number from params is in valid format
          patient_phone_number: isPhoneNumberValid(patientPhoneNumber)
            ? patientPhoneNumber
            : '',
        })
      );
    }
  }, [patientId, patientName, patientPhoneNumber]);

  const handleSubmit = () => {
    if (!doctorID) return;
    dispatch(
      storeConsultation({
        doctor: doctorData,
        consultation_date: selectedDate.date,
        consultation_time: selectedTimes,
        is_with_appointment: selectedDate.is_with_appointment,
      })
    );
    history.push(`/patient/consultation-select-patient?lng=${currentLanguage}`);
  };

  useEffect(() => {
    // NOTE: Reset times on date change
    if (!consultationData?.consultation_time?.length) {
      setSelectedTimes([]);
    }
  }, [selectedDate]);

  const onBack = () => {
    window.location.href = 'https://lifepack.id/konsultasi-psikiater/';
  };

  return (
    <MainLayout
      hideBanner
      style={{
        backgroundColor: 'white',
        padding: '0px',
        marginBottom: '80px',
      }}
    >
      {/* ANCHOR - Header */}
      <Box
        textAlign="center"
        boxShadow={HEADER_BOXSHADOW_VALUE}
        p="16px"
        display="flex"
        justifyContent="space-between"
        position="relative"
      >
        <div onClick={() => onBack()} style={{ cursor: 'pointer' }}>
          <img width="12px" src={arrowLeft} alt="arrow_left" />
        </div>
        <h2 className={style.title}>{translate('scheduleConsultation')}</h2>
        <div />
      </Box>
      <Box bgcolor="#dadada" textAlign="center" height={200}>
        <img
          src={doctorData?.image_url || avatarDoctorImage}
          alt="Doctor"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>
      <Box
        p={2}
        display="flex"
        flexDirection="column"
        borderBottom="1px solid #EEF0F0"
        style={{ gap: '6px' }}
      >
        <p className={style.name}>{doctorData?.doctor_name}</p>
        <p className={style.specialist}>
          {doctorData?.doctor_speciality[currentLanguage ?? 'id']}
        </p>
        <Box display="flex" style={{ gap: 4 }}>
          <img src={locationImage} alt="location" />
          <p className={style.city}>
            {doctorData?.city_name[currentLanguage ?? 'id'] || '-'}
          </p>
        </Box>
      </Box>
      <Box p={2} fontWeight={700}>
        <p className="m-0">
          {currencyHelper(doctorData?.consultation_fee || 0)}
        </p>
      </Box>
      <Divider />
      {/* ANCHOR - Schedule */}
      <Box p={2} mb={2} fontWeight={700}>
        <p className="m-0">{translate('selectDateAndTime')}</p>
        <DateSection
          setSelectedDate={setSelectedDate}
          selectedDate={selectedDate}
          schedules={doctorData?.schedules || []}
        />
        <Box bgcolor="#FAFAFA" height="1px" width="100%" my={2} />
        {selectedDate?.is_with_appointment && (
          <Box
            display="flex"
            borderRadius={8}
            bgcolor="#F1F5FF"
            p={1}
            mb={2}
            alignItems="center"
          >
            <img src={timeImage} alt="keterangan lanjutan" />
            <p
              className="font-size-14 font-weight-400 m-0 ml-8"
              style={{ opacity: 0.8, color: '#363655' }}
            >
              {translate('youCanSelectSeveralConsultation')}
            </p>
          </Box>
        )}
        <TimeSection
          setSelectedTimes={setSelectedTimes}
          selectedTimes={selectedTimes}
          selectedDate={selectedDate}
          schedules={doctorData?.schedules}
          isWithAppointment={selectedDate?.is_with_appointment}
        />
      </Box>
      <Divider />
      {/* ANCHOR - Profil */}
      <Box p={2}>
        <p className="mb-8 font-weight-800">{translate('doctorProfile')}</p>
        <p className="m-0 font-size-14">
          {doctorData?.profile[currentLanguage ?? 'id'] || '-'}
        </p>
      </Box>
      {/* ANCHOR - Floating Footer */}
      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        display="flex"
        width="100%"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          maxWidth="600px"
          width="100%"
          px={4}
          py={2}
          display="flex"
          justifyContent="space-between"
          bgcolor="white"
          boxShadow={FOOTER_BOXSHADOW_VALUE}
        >
          <Box flex={1}>
            <p className="m-0 font-size-12">{translate('doctorFee')}</p>
            <p className="m-0 font-weight-800">
              {currencyHelper(doctorData?.consultation_fee || 0)}
            </p>
          </Box>
          <Button
            borderRadius="100%"
            size="large"
            style={{
              flex: 1,
              width: 'auto',
              margin: 0,
              borderRadius: '24px',
              padding: '12px 24px',
            }}
            onClick={handleSubmit}
            disabled={selectedTimes.length === 0 || !selectedDate?.date}
          >
            {translate('next')}
          </Button>
        </Box>
      </Box>
    </MainLayout>
  );
}
